import { Row } from '@devexpress/dx-react-grid';
import { MainPageContainer, UpperInfoContainer } from 'components/PageLayout';
import ReportGrid from 'components/ReportGrid';
import { createReportURL } from 'core/routes';
import { convertToDate, useEmailReport } from 'pages/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReportProps, useReportParams } from '../hooks';
import { dateToString, formatDate } from 'utils/helpers';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import { AxiosError } from 'axios';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import { ReportGridContainer } from 'components/ReportGridContainer';
import { columns, dateColumnsName, summaryItems, tableColumnExtensions, EMAIL_REPORT, EMAIL_INFO_MESSAGE } from './constants';
import { SummaryItemComponent } from 'components/ReportsComponents/SummaryItemComponent';
import { DateTypeProvider } from 'components/ReportsComponents/DateTypeProvider';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import { customizeCell, valuesConverter } from './helpers';
import { ReportSearchForm } from 'components/ReportForms/ReportSearchForm';
import { reportLogger } from '../reportLogger';
import { customizeCountSummaryCell } from '../helpers';
import { mockedEmailReports } from 'pages/ReportsDetails_NEW/EmailReport/constants';

export function EmailReportArchived({
  reportPath,
  setErrorMessage,
  setToast,
}: ReportProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = useReportParams(location.search);
  const [startDate, setStartDate] = useState<Date | null>(
    convertToDate(urlParams.startDate) ?? null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    convertToDate(urlParams.endDate) ?? null
  );

  const queryEnabled = !!urlParams.startDate && !!urlParams.endDate;
  const { reportData, refetchReport, isReportFetching } = useEmailReport(
    {
      startDate: urlParams.startDate,
      endDate: urlParams.endDate,
    },
    queryEnabled,
    handleError
  );

  const rows = useMemo<Row>(() => reportData, [reportData]);
    // const rows = useMemo<Row>(() => mockedEmailReports, []); // TODO: remove after testing


  useEffect(() => {
    navigateToReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.startDate, urlParams.endDate]);

  return (
    <MainPageContainer
      isLoading={isReportFetching}
      direction="column"
      wrap="nowrap"
    >
      <UpperInfoContainer direction="column">
        <ReportBackButton />
        <ReportHeader
          reportName={EMAIL_REPORT}
          infoMessage={EMAIL_INFO_MESSAGE}
        />
        <ReportSearchForm
          handleSubmit={handleSubmit}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          isDisabled={isReportFetching}
        />
      </UpperInfoContainer>

      {!isReportFetching && rows?.length === 0 && <NoReportDataMessage />}

      <ReportGridContainer direction="column">
        <ReportGrid
          rows={rows}
          columns={columns}
          title="emailReport"
          isLoading={isReportFetching}
          summaryItems={summaryItems}
          dateColumnsName={dateColumnsName}
          customizeCellExport={customizeCell}
          DateTypeProvider={DateTypeProvider}
          customizeSummaryCell={customizeCountSummaryCell}
          SummaryItemComponent={SummaryItemComponent}
          tableColumnExtensions={tableColumnExtensions}
          headerTitleForSheet="Email Report"
          reportHeaderTagLine={`Report produced on ${formatDate(
            new Date()
          )} for period ${formatDate(startDate)} to ${formatDate(endDate)}`}
          pdfOrientation='l'
          showAdditionalExportOptions
          valuesConverter={valuesConverter}
        />
      </ReportGridContainer>
    </MainPageContainer>
  );

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (
      urlParams.startDate == dateToString(startDate) &&
      urlParams.endDate == dateToString(endDate)
    )
      refetchReport();
    else navigateToReport();
  }

  function navigateToReport() {
    if (!!startDate || !!endDate)
      navigate(
        createReportURL(
          {
            startDate: dateToString(startDate),
            endDate: dateToString(endDate),
          },
          reportPath,
          true
        ), { replace: true });
  }

  function handleError(error: AxiosError) {
    reportLogger(error);
    setErrorMessage(errorMessages.reports('Email Report'));
    setToast(true);
  }
}
